import { useEffect, useMemo, useRef, useState } from "react";
import useAxios from "../utils/useAxios";
import { useAppContext } from "../context/AppContext";
import AsyncSelect from "react-select/async";
import { showModal, hideModal } from "../utils/modalUtility";
import Pagination from "../components/common/Pagination";
import Loader from "../components/common/Loader";

const INITIAL_FORM_DATA = {
  item_issued: "",
  occasion: "",
  issuing_date: "",
  user_id: "",
};

const INITIAL_FILTERS = {
  search: "",
  agency: "",
  from_date: "",
  to_date: "",
};

function Gifts() {
  const [loading, setLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [role, setRole] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [users, setUsers] = useState([]); // Fill this via API on mount
  const [view, setView] = useState("excel"); // default: show Excel section
  const axiosAPI = useAxios();
  const [excelFile, setExcelFile] = useState(null);
  const [message, setMessage] = useState("");
  const [shouldFetch, setShouldFetch] = useState(false);

  const fileInputRef = useRef();

  const [uploadRows, setUploadRows] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const { state } = useAppContext();
  const { data, loading: donorLoading } = state;

  const [addGiftLoading, setAddGiftLoading] = useState(false);
  const [masterGifts, setMasterGifts] = useState([]);
  const [masterOccasions, setMasterOccasions] = useState([]);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [giftId, setGiftId] = useState(null);

  const [masterGiftFormData, setMasterGiftFormData] = useState({
    gift_name: "",
  });

  const filteredOptions = useMemo(() => {
    return data
      .filter((user) => {
        const nameMatch = user.full_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase());
        const phoneMatch = user.phone_number
          ?.toString()
          .includes(searchTerm.toLowerCase());
        return nameMatch || phoneMatch;
      })
      .slice(0, 50)
      .map((user) => ({
        value: user.id,
        label: `${user.full_name} - ${user.phone_number || ""}`,
      }));
  }, [data, searchTerm]);

  // console.log("Selected User", selectedUser);
  // console.log("Search Term", searchTerm);
  // console.log("Filter Option", filteredOptions);

  const loadOptions = (inputValue, callback) => {
    setSearchTerm(inputValue);
    callback(filteredOptions);
  };

  const handleAddGift = async () => {
    const giftName = masterGiftFormData?.gift_name?.trim();

    if (!giftName) {
      window.notifyMessage("Error", "Gift name is required", "error");
      return;
    }

    try {
      const payload = { gift_name: giftName };

      const response = await axiosAPI.post(
        "/api/gifts/add-master-gift",
        payload,
      );
      const data = response.data;

      // Optional: double check status code
      if (![200, 201].includes(response.status)) {
        throw new Error(data.message || "Something went wrong");
      }
      // Show success message
      window.notifyMessage("Success", "Gift added successfully", "success");

      // Optionally add to frontend list (if you’re maintaining one)
      // const newGift = { id: data.giftId || Date.now(), name: giftName };
      // setGiftList((prev) => [...prev, newGift]);

      // Clear input
      fetchMasterGift();
      setMasterGiftFormData({ gift_name: "" });
    } catch (error) {
      console.error("Failed to add gift:", error.message);
      window.notifyMessage(
        "Error",
        error.message || "Failed to add gift",
        "error",
      );
    }
  };

  const handleDeleteGift = async (id) => {
    try {
      const isConfirmed = window.confirm(
        "Are you sure you want to delete this gift?",
      );
      if (!isConfirmed) return;

      const response = await axiosAPI.delete(
        `/api/gifts/delete-master-gift/${id}`,
      );
      const data = response.data;

      // Optional: double check status code
      if (![200, 204].includes(response.status)) {
        throw new Error(data.message || "Something went wrong");
      }

      window.notifyMessage("Success", "Gift deleted successfully", "success");

      setMasterGifts((prevList) => prevList.filter((gift) => gift.id !== id));

      //  Refresh the list after deletion
      // fetchMasterGift();
    } catch (error) {
      console.error("Failed to delete gift:", error.message);
      window.notifyMessage(
        "Error",
        error.message || "Failed to delete gift",
        "error",
      );
    }
  };

  async function fetchMasterGift() {
    //  setLoading(true); // Start loading
    try {
      const res = await axiosAPI.get(`/api/gifts/get-master-gifts`);
      // console.log("Gifts", res.data);
      setMasterGifts(res.data.results);
    } catch (err) {
      console.error("Error fetching gifts:", err);
    } finally {
      setLoading(false); // Always stop loading
    }
  }

  async function fetchMasterOccasion() {
    //  setLoading(true); // Start loading
    try {
      const res = await axiosAPI.get(`/api/gifts/get-master-occasions`);
      setMasterOccasions(res?.data?.results);
    } catch (err) {
      console.error("Error fetching occasions:", err);
    } finally {
      setLoading(false); // Always stop loading
    }
  }

  function handleMasterGift(e) {
    const { name, value } = e.target;
    // console.log(name, value);
    setMasterGiftFormData((prev) => ({ ...prev, [name]: value }));
  }

  useEffect(() => {
    if (formData.user_id) {
      // filteredOptions is your array of options or you can fetch full user info
      const option =
        filteredOptions.find((opt) => opt.value === formData.user_id) || null;
      setSelectedUser(option);
    } else {
      setSelectedUser(null);
    }
  }, [formData.user_id, filteredOptions]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
  };

  // console.log("Data", data);

  const openUploadDonorModal = () => {
    // console.log("Inside Modal");
    showModal("newGiftExcelModal");
  };
  const openMasterGiftModal = () => {
    showModal("openMasterGiftModal");
    // console.log("Modal Open");
  };

  useEffect(() => {
    if (shouldFetch) {
      fetchGifts();
      setShouldFetch(false); // reset trigger
    }
  }, [shouldFetch]);

  const handleExcelFileChange = (e) => {
    const file = e.target.files[0];
    if (
      file &&
      file.type !==
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      alert("Please upload a valid Excel file (.xlsx)");
      setExcelFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null; // Reset input field
      }
    } else {
      setExcelFile(file);
    }
  };

  const handleExcelSubmit = async (e) => {
    e.preventDefault();

    if (!excelFile) {
      alert("Please select an Excel file.");
      return;
    }

    const formData = new FormData();
    formData.append("excel", excelFile);

    try {
      const response = await axiosAPI.post("/api/gifts/upload-gift", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // console.log(response.data);

      //Store the rows for displaying summary table

      const {
        message = "",
        insertedCount = 0,
        rows = [],
        failedExcelBase64,
      } = response.data;

      // ⬇ Add this next
      if (failedExcelBase64) {
        const binaryString = atob(failedExcelBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "failed_rows.xlsx"; // You can change this filename if needed
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setUploadRows(rows);
      const failedRows = rows.filter((r) => r.status === "fail");
      const passedRows = rows.filter((r) => r.status === "pass");

      // console.log("Upload Summary:", {
      //   insertedCount,
      //   passed: passedRows.length,
      //   failed: failedRows.length,
      // });

      //  Fix: Check if rows were even processed
      if (rows.length === 0) {
        window.notifyMessage(
          "Error",
          "No valid data found in Excel file.",
          "error",
        );
      } else if (insertedCount === 0 && failedRows.length > 0) {
        window.notifyMessage(
          "Upload Failed",
          `No rows were inserted.\nFailed Rows: ${failedRows.length}`,
          "error",
        );
        console.table(failedRows);
      } else if (failedRows.length > 0) {
        window.notifyMessage(
          "Partial Success",
          `${message}\nInserted: ${insertedCount}, Failed: ${failedRows.length}`,
          "warning",
        );
        console.table(failedRows);
      } else {
        window.notifyMessage(
          "Success",
          "Excel file uploaded successfully!",
          "success",
        );
      }

      // closeModal();
      // hideModal("newGiftExcelModal");
      fetchGifts();

      if (fileInputRef.current) fileInputRef.current.value = null;

      setExcelFile(null);
      setMessage("");
    } catch (err) {
      console.error("Excel Upload Error:", err);

      const errorMsg =
        err?.response?.data?.message ||
        "Something went wrong while uploading the Excel file.";

      window.notifyMessage("Error", errorMsg, "error");

      if (err.response?.data?.error) {
        console.error("Backend Error:", err.response.data.error);
      }
    }
  };

  async function changeGiftStatus(e, giftId) {
    const currentStatus = e.target.value;

    // console.log("gift Id", giftId);
    // console.log("Current Status", currentStatus);

    try {
      const isConfirmed = window.confirm(
        "Are you sure you want to change the gift status?",
      );
      if (!isConfirmed) return;

      const response = await axiosAPI.put(
        `/api/gifts/change-gift-status/${giftId}`,
        { status: currentStatus },
      );

      fetchGifts();
      window.notifyMessage(
        "Success",
        "Gift status changed successfully",
        "success",
      );
    } catch (error) {
      console.error("Error changing gift status:", error);
      window.notifyMessage(
        "Error",
        "Failed to change gift status. Please try again.",
        "danger",
      );
    }
  }

  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const handleResetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setShouldFetch(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // console.log("agency ", filters.agency);
  // console.log("From date ", filters.from_date);
  // console.log("To date ", filters.to_date);

  const buildQuery = () => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    params.append("page", page);
    return params.toString();
  };

  async function fetchGifts() {
    setLoading(true); // Start loading
    const queryString = buildQuery();

    try {
      const res = await axiosAPI.get(
        `/api/gifts/get-gifts-from-db?${queryString}`,
      );
      // console.log("Gifts", res.data);
      setGifts(res.data.results);
      setTotalPages(res.data.totalPages);
    } catch (err) {
      console.error("Error fetching gifts:", err);
    } finally {
      setLoading(false); // Always stop loading
    }
  }

  useEffect(() => {
    // const role = JSON.parse(localStorage.getItem("user_role")); // Parses string "admin" to plain string
    setRole(state?.user?.role);
  }, []);

  // Get All Donor Details
  const getDonorDetails = async () => {
    try {
      const response = await axiosAPI.get("/api/donor/get-donor-details");
      setUsers(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGifts();
    getDonorDetails();
    fetchMasterGift();
    fetchMasterOccasion();
  }, [page]);

  const downloadExcel = async (table_name) => {
    setDownloading(true); //  Start loader
    const queryString = buildQuery();
    try {
      const response = await axiosAPI.get(
        `/api/gifts/download-gift-excel?table=${table_name}&${queryString}`, //  Pass table name in query
        {
          responseType: "blob",
        },
      );

      if (response.status !== 200) {
        throw new Error("Failed to download file");
      }

      const blob = response.data;
      const url = window.URL.createObjectURL(new Blob([blob]));

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${table_name}.xlsx`); // Optional: dynamic file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download file. Please try again later.");
    } finally {
      setDownloading(false); //  End loader
    }
  };

  const handlePageClick = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) {
      setPage(pageNum);
    }
  };

  // Handle Submit (Add or Edit)
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      user_id: formData.user_id?.value,
      occasion: formData.occasion,
      item_issued: formData.item_issued,
      issuing_date: formData.issuing_date,
    };

    // console.log("Payload ", payload);

    // return;

    setAddGiftLoading(true);

    try {
      let response;
      if (isEditMode) {
        response = await axiosAPI.put(
          `/api/gifts/update-gift/${currentUserId}`,
          payload,
        );
        window.notifyMessage("Success", "Gift updated successfully", "success");
      } else {
        response = await axiosAPI.post("/api/gifts/add-gift", payload);
        window.notifyMessage("Success", "Gift added successfully", "success");
      }
      hideModal("newUserModal");
      fetchGifts();
      resetForm();
      setIsEditMode(false);
      // window.location.reload(); // Reload page to reflect changes in the notification list
      setAddGiftLoading(false);
      closeModal();
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        window.notifyMessage("Error", error.response.data.message, "danger");
      } else {
        window.notifyMessage("Error", "There was an error!", "danger");
      }
    }
  };

  // Edit Modal Open
  const openEditModal = (id) => {
    setIsEditMode(true);
    setCurrentUserId(id);

    // console.log("gifts", gifts);

    // Find the Prashdam with this ID and populate the form
    const selectedGift = gifts.find((gift) => gift.id === id);

    // console.log("id", id);
    // console.log("selected gift", selectedGift);

    if (selectedGift) {
      const selectedUser = users.find(
        (user) => user.id === selectedGift.user_id,
      );

      // console.log("Select User", selectedUser);
      setFormData({
        item_issued: selectedGift.item_issued,
        occasion: selectedGift.occasion,
        issuing_date: selectedGift.issuing_date,
        user_id: selectedUser
          ? { value: selectedUser?.id, label: selectedUser?.full_name }
          : null,
      });
    }

    const modal = new window.bootstrap.Modal(
      document.getElementById("newUserModal"),
    );
    modal.show();
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm("Are you sure you want to delete this?");
    if (!isConfirmed) return;

    try {
      const response = await axiosAPI.delete(`/api/gifts/delete-gift/${id}`);
      window.notifyMessage("Success", "Deleted successfully", "success");
      fetchGifts();
    } catch (error) {
      console.error("Error deleting:", error);
      window.notifyMessage(
        "Error",
        "An error occurred while deleting.",
        "danger",
      );
    }
  };

  // Close Modal
  const closeModal = () => {
    setIsEditMode(false);
    resetForm();
    setUploadRows("");
    // setCurrentUserId(null);
  };

  const openAddModal = () => {
    showModal("newUserModal");
  };

  return (
    <div className="container">
      <div className="page-inner">
        <div className="d-flex justify-content-end align-items-center flex-wrap gap-2 mb-4">
          {/* {role === "admin" && (
            <button
              type="button"
              className="btn btn-success btn-round"
              onClick={() => openMasterGiftModal()}
            >
              Add Master Gifts
            </button>
          )} */}
          {/*  Right Side - Existing Buttons */}
          {role === "admin" && (
            <div className="d-flex justify-content-between gap-2 flex-wrap">
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-success btn-round"
                  onClick={() => {
                    if (
                      filters.search ||
                      (filters.from_date && filters.to_date)
                    ) {
                      downloadExcel("gifts");
                    } else {
                      const confirmFullDownload = window.confirm(
                        "No filters applied. Do you want to download the full sheet?",
                      );
                      if (confirmFullDownload) {
                        downloadExcel("gifts");
                      }
                    }
                  }}
                  disabled={downloading}
                >
                  Export Gifts
                </button>

                <button
                  type="button"
                  className="btn btn-success btn-round"
                  onClick={() => openUploadDonorModal()}
                >
                  Upload Gifts
                </button>

                <button
                  type="button"
                  className="btn btn-dark btn-round"
                  onClick={() => openAddModal()}
                >
                  Add Gifts
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Modal */}
        <div
          className="modal fade"
          id="newUserModal"
          tabIndex="-1"
          aria-labelledby="newUserModalLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newUserModalLabel">
                  {isEditMode ? "Edit Gift" : "Add Gift"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  {/* User Dropdown */}
                  <div className="mb-3">
                    <label htmlFor="user_id" className="form-label">
                      Select Donor
                    </label>
                    <AsyncSelect
                      cacheOptions
                      defaultOptions={filteredOptions}
                      loadOptions={loadOptions}
                      // onChange={(selectedOption) =>
                      //   setFormData((prev) => ({
                      //     ...prev,
                      //     user_id: selectedOption ? selectedOption.value : null,
                      //   }))
                      // }
                      // value={
                      //   filteredOptions.find((opt) => opt.value === formData.user_id) || null
                      // }
                      onChange={(selectedOption) =>
                        setFormData((prev) => ({
                          ...prev,
                          user_id: selectedOption,
                        }))
                      }
                      value={formData.user_id}
                      isClearable
                      placeholder="-- Select Donor --"
                    />
                  </div>

                  {/* Item Issued */}
                  {/* <div className="mb-3">
                    <label htmlFor="item_issued" className="form-label">
                      Item Issued
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="item_issued"
                      name="item_issued"
                      placeholder="Enter the item issued"
                      value={formData.item_issued}
                      onChange={handleChange}
                      required
                    />
                  </div> */}

                  <div className="mb-3">
                    <label htmlFor="item_issued" className="form-label">
                      Item Issued
                    </label>

                    <select
                      className="form-select"
                      id="item_issued"
                      name="item_issued"
                      value={formData.item_issued}
                      onChange={handleChange}
                      required
                    >
                      <option value="">-- Select an item --</option>
                      {masterGifts?.map((item) => (
                        <option key={item.id} value={item.name}>
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Occasion */}
                  {/* <div className="mb-3">
                    <label htmlFor="occasion" className="form-label">
                      Occasion
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="occasion"
                      name="occasion"
                      placeholder="Enter occasion"
                      value={formData.occasion}
                      onChange={handleChange}
                      required
                    />
                  </div> */}
                  <div className="mb-3">
                    <label htmlFor="occasion" className="form-label">
                      Occasion
                    </label>

                    <select
                      className="form-select"
                      id="occasion"
                      name="occasion"
                      value={formData.occasion}
                      onChange={handleChange}
                      required
                    >
                      <option value="">-- Select an occasion --</option>
                      {masterOccasions?.map((item) => (
                        <option key={item.id} value={item.occasion_name}>
                          {item.occasion_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Issuing Date */}
                  <div className="mb-3">
                    <label htmlFor="issuing_date" className="form-label">
                      Issuing Date
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="issuing_date"
                      name="issuing_date"
                      value={formData.issuing_date}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary btn-round btn-sm"
                  >
                    {addGiftLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {isEditMode ? "Updating..." : "Adding..."}
                      </>
                    ) : isEditMode ? (
                      "Update Gift"
                    ) : (
                      "Add Gift"
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>

        {downloading && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              className="spinner-border text-success"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}

        {/* Add Donor Modal */}
        <div
          className="modal fade"
          id="newGiftExcelModal"
          tabIndex="-1"
          aria-labelledby="newGiftExcelModal"
          aria-hidden="true"
        >
          <div
            className={`modal-dialog ${
              view === "manual"
                ? "modal-fullscreen"
                : "modal-dialog-scrollable modal-dialog-centered modal-md"
            }`}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="newGiftExcelModal">
                  {isEditMode ? "Edit Gift" : "Add Gift"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={() => {
                    closeModal();
                    setView("excel");
                  }}
                ></button>
              </div>

              <div className="modal-body">
                {view === "excel" && (
                  <>
                    {/* Excel Upload UI */}
                    <div className="mb-3">
                      <label htmlFor="excelUpload" className="form-label">
                        Upload Excel File (.xlsx)
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        id="excelUpload"
                        accept=".xlsx"
                        ref={fileInputRef}
                        onChange={handleExcelFileChange}
                      />
                      <div className="mt-3">
                        <button
                          type="button"
                          className="btn btn-link p-0 text-primary"
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = "/samples/gifts_sample.xlsx";
                            link.download = "gifts_sample.xlsx";
                            link.click();
                          }}
                        >
                          Download Sample Sheet
                        </button>
                      </div>

                      <button
                        className="btn btn-success mt-3"
                        onClick={handleExcelSubmit} // Submit to backend
                      >
                        Upload
                      </button>
                      {message && <div className="mt-2">{message}</div>}
                      {/* Display any messages */}

                      {uploadRows.length > 0 && (
                        <div className="mt-3">
                          <h5>Upload Summary:</h5>
                          <table className="table table-bordered table-striped">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Row</th>
                                <th>Status</th>
                                <th>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {uploadRows.map((rowData, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{rowData?.row}</td>
                                  <td
                                    style={{
                                      color:
                                        rowData?.status === "fail"
                                          ? "red"
                                          : "green",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    {rowData?.status}
                                  </td>
                                  <td>{rowData?.reason || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Gifts Table */}
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-end flex-wrap gap-3">
                {/* Title */}
                <h4 className="card-title mb-0">All Gifts ({gifts.length})</h4>

                {/* Filter section - horizontal layout */}
                <div className="d-flex align-items-end gap-2 flex-wrap">
                  {role === "admin" && (
                    <div
                      className="position-relative"
                      style={{ width: "310px" }}
                    >
                      <input
                        className="form-control shadow-sm px-3 py-2  border-2 w-100"
                        type="text"
                        placeholder="Enter name or phone_number or donor ID..."
                        value={filters.search}
                        onChange={(e) =>
                          handleFilterChange("search", e.target.value)
                        }
                        style={{ paddingRight: "110px" }}
                      />
                    </div>
                  )}
                  {/* From Date */}
                  <div className="d-flex flex-column">
                    <label htmlFor="fromDate" className="form-label mb-1">
                      From
                    </label>
                    <input
                      id="fromDate"
                      type="date"
                      className="form-control"
                      value={filters.from_date || ""}
                      onChange={(e) =>
                        handleFilterChange("from_date", e.target.value)
                      }
                    />
                  </div>

                  {/* To Date */}
                  <div className="d-flex flex-column">
                    <label htmlFor="toDate" className="form-label mb-1">
                      To
                    </label>
                    <input
                      id="toDate"
                      type="date"
                      className="form-control"
                      value={filters.to_date || ""}
                      onChange={(e) =>
                        handleFilterChange("to_date", e.target.value)
                      }
                    />
                  </div>

                  {/* Filter Button */}
                  <div className="d-flex flex-column">
                    <button
                      className="btn btn-primary rounded-pill px-4"
                      onClick={() => {
                        if (
                          filters.search ||
                          (filters.from_date && filters.to_date)
                        ) {
                          setPage(1);
                          fetchGifts();
                        } else {
                          alert("Please select both From and To dates.");
                        }
                      }}
                    >
                      Filter
                    </button>
                  </div>
                  <div className="d-flex flex-column">
                    <button
                      className="btn btn-icon btn-round btn-dark py-3 px-3  me-2"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Reset Filters"
                      onClick={handleResetFilters}
                    >
                      <i className="fas fa-sync-alt"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>full name</th>
                        <th>Donor ID</th>
                        <th>Occasion</th>
                        <th>Status</th>
                        <th>ITEM issued</th>
                        {/* <th>Scheme Patronship No. </th> */}
                        <th>issued Date</th>

                        {/* <th>Modified Time</th> */}
                        {/* <th>Created At</th> */}
                        {/* <th>Action</th> */}
                        {role === "admin" && (
                          <th>Action</th> // Show this if the role is "admin"
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <Loader />
                        </tr>
                      ) : gifts.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No Data Found
                          </td>
                        </tr>
                      ) : (
                        gifts.map((gifts) => {
                          const formattedDate = new Date(
                            gifts.issuing_date,
                          ).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          });

                          return (
                            <tr key={gifts.id}>
                              <td>{gifts.full_name}</td>
                              <td>{gifts.donor_id}</td>
                              <td>{gifts.occasion}</td>
                              {role == "admin" ? (
                                <td>
                                  <select
                                    className="form-select"
                                    value={gifts.status}
                                    onChange={(e) =>
                                      changeGiftStatus(e, gifts?.id)
                                    }
                                  >
                                    <option value="PENDING">Pending</option>
                                    <option value="DISPATCHED">
                                      Dispatched
                                    </option>
                                    <option value="DELIVERED">Delivered</option>
                                  </select>
                                </td>
                              ) : (
                                <td>{gifts.status}</td>
                              )}
                              <td>{gifts.item_issued}</td>
                              {/* <td>{gifts.contact_id}</td> */}
                              {/* <td>{gifts.gift_id}</td> */}

                              {/* <td>{truncatedMessage}</td> */}
                              <td>{formattedDate}</td>
                              <td>
                                {role === "admin" && (
                                  <button
                                    className="btn btn-icon btn-round btn-dark btn-sm me-2"
                                    onClick={() => openEditModal(gifts.id)}
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                )}
                                {/* <button
                                  className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                  onClick={() => handleDelete(gifts.id)}
                                >
                                  <i className="fas fa-trash"></i>
                                </button> */}

                                {/* {role === "admin" && (
                                  <button
                                    className="btn btn-icon btn-round btn-danger btn-sm me-2"
                                    onClick={() => handleDelete(gifts.id)}
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                )} */}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Gifts;
